import { useRef, useCallback } from 'react';
import { throttle } from 'lodash';

/**
 * Hook that returns a throttled version of the provided function
 * Using lodash's throttle for better performance and reliability
 * @param fetchFn The function to throttle
 * @param delay Minimum time between function calls in ms
 * @returns Throttled version of fetchFn
 */
export const useThrottledFetch = <T extends (...args: any[]) => any>(
  fetchFn: T,
  delay: number = 2000,
) => {
  // Store the throttled function in a ref to maintain the same reference
  const throttledFnRef = useRef<ReturnType<typeof throttle> | null>(null);

  // Initialize the throttled function if not yet created
  if (!throttledFnRef.current) {
    throttledFnRef.current = throttle((...args: Parameters<T>) => fetchFn(...args), delay, {
      leading: true,
      trailing: false,
    });
  }

  // Return a stable callback that uses the throttled function
  return useCallback(
    (...args: Parameters<T>) => throttledFnRef.current?.(...args),
    [fetchFn, delay], // Dependencies ensure we recreate if fetch function or delay changes
  );
};

export default useThrottledFetch;
